import { AGENCIES_ROUTE, PUBLISHERS_ROUTE } from 'components/constants';
import { IndustrySolution, industrySolutionBySlug } from 'components/pages/IndustrySolutions/data';

export type SolutionCard = Pick<
  IndustrySolution,
  | 'slug'
  | 'route'
  | 'name'
  | 'cardTitle'
  | 'cardText'
  | 'cardProducts'
  | 'cardCta'
  | 'accent'
  | 'softAccent'
>;

export const agencySolution: IndustrySolution = {
  slug: 'agencies',
  route: AGENCIES_ROUTE,
  name: 'Advertising agencies',
  cardTitle: 'Advertising agencies',
  cardText: 'Take client ideas from brief to branded, measurable experiences without rebuilding every campaign.',
  cardProducts: ['Campaign experience', 'Product advisor', 'Branded minisite'],
  cardCta: 'Explore agency solutions',
  accent: '#6f46b8',
  softAccent: '#eee7fa',
  seo: {
    title: 'Interactive Experience Platform for Agencies | Storycards',
    description:
      'Build campaign experiences, product advisors and branded minisites in one governed production platform for agency teams and their clients.',
  },
  hero: {
    eyebrow: 'Storycards for advertising agencies',
    title: 'Build the idea you pitched, not a smaller version of it.',
    text:
      'Give creative teams the tools to design, build, publish and improve complete interactive products for every client. Keep brand systems, client collaboration, data and delivery connected from brief to live experience.',
    previewLabel: 'Campaign experience',
    previewQuestion: 'What should this launch help people do?',
    previewOptions: ['Discover the story', 'Find the right product', 'Register interest'],
    previewResult: 'A client-ready journey with approved design, interaction and measurement',
  },
  outcomesTitle: 'More creative range. A production model agencies can repeat.',
  outcomes: [
    {
      title: 'Show the idea as a working product',
      text: 'Turn the pitch into an experience clients can explore, review and understand before production is complete.',
    },
    {
      title: 'Keep every client system separate',
      text: 'Manage approved components, templates, permissions and products for multiple brands in one organized workspace.',
    },
    {
      title: 'Continue after launch',
      text: 'Use interaction data and drop-off points to make focused improvements and extend the value of the engagement.',
    },
  ],
  productsTitle: 'Products agencies can take all the way to launch.',
  productsIntro:
    'Move beyond mockups and static campaign assets. Each product can launch as a standalone destination or become part of the client’s existing website or app.',
  products: [
    {
      title: 'Interactive brand experience',
      text: 'Combine storytelling, video, interaction and data in a product that stays true to the client’s visual system.',
      signal: 'Audience engagement',
    },
    {
      title: 'Product advisor',
      text: 'Guide customers through a useful set of questions and route them to a relevant product or next action.',
      signal: 'Qualified intent',
    },
    {
      title: 'Launch minisite',
      text: 'Build a responsive campaign destination with its own story, interaction model and conversion path.',
      signal: 'Launch behavior',
    },
    {
      title: 'Interactive magazine',
      text: 'Bring articles, profiles, video and sponsor integrations together in a premium branded publication.',
      signal: 'Content interests',
    },
    {
      title: 'Lead qualification journey',
      text: 'Collect the context a client’s sales or service team needs before the audience reaches the handoff.',
      signal: 'Lead context',
    },
    {
      title: 'Embedded campaign product',
      text: 'Add a quiz, finder, registration or explainer inside an existing client page without creating a separate stack.',
      signal: 'Embedded events',
    },
  ],
  journey: {
    eyebrow: 'From brief to live product',
    title: 'Keep the creative idea connected to production.',
    text:
      'Use one workflow to make the concept tangible, protect the client brand and carry the work through publishing and measurement.',
    steps: [
      {
        label: '01',
        title: 'Shape the concept',
        text: 'Start from a brief, prompt, Figma design, reusable template or blank canvas.',
      },
      {
        label: '02',
        title: 'Build the experience',
        text: 'Control responsive layouts, states, motion, logic and data collection in the professional Studio.',
      },
      {
        label: '03',
        title: 'Review with the client',
        text: 'Use roles, protected editing and clear approval ownership to keep feedback inside the production system.',
      },
      {
        label: '04',
        title: 'Publish and improve',
        text: 'Launch standalone or embedded, then use measured behavior to guide the next change.',
      },
    ],
  },
  operatingTitle: 'One production system across agency and client teams.',
  operatingText:
    'The agency protects creative quality while each client keeps control of its brand, content, access and approved data destinations.',
  roles: [
    { team: 'Creative', responsibility: 'Own the concept, art direction, interaction and responsive experience.' },
    { team: 'Production', responsibility: 'Build reusable components, logic and publishing-ready products.' },
    { team: 'Account teams', responsibility: 'Keep briefs, review stages and client decisions connected to delivery.' },
    { team: 'Client teams', responsibility: 'Review, approve and update the content areas they are allowed to change.' },
    { team: 'Data and media', responsibility: 'Define events, measurement and approved handoffs into the client stack.' },
  ],
  related: ['retail', 'real-estate'],
  cta: {
    title: 'Build the idea you sold. Deliver the product your client expects.',
    text: 'Bring one brief or campaign concept. We will help you map the experience, production system and launch path.',
  },
};

export const publisherSolution: IndustrySolution = {
  slug: 'publishers',
  route: PUBLISHERS_ROUTE,
  name: 'Publishers and media',
  cardTitle: 'Publishers & media',
  cardText: 'Turn editorial and commercial stories into interactive products audiences can explore and respond to.',
  cardProducts: ['Interactive article', 'Live tracker', 'Sponsor experience'],
  cardCta: 'Explore publisher solutions',
  accent: '#b64f17',
  softAccent: '#f8e9df',
  seo: {
    title: 'Interactive Publishing and Media Platform | Storycards',
    description:
      'Create interactive articles, live audience products and sponsor-ready experiences that embed into publisher sites and apps.',
  },
  hero: {
    eyebrow: 'Storycards for publishers and media',
    title: 'Turn every story into a product audiences can explore.',
    text:
      'Create premium editorial products, interactive articles and embedded experiences for planned stories, live moments and commercial partnerships. Keep design, publishing and audience data under your control.',
    previewLabel: 'Interactive story',
    previewQuestion: 'How should readers take part?',
    previewOptions: ['Explore the timeline', 'Vote on the question', 'Compare the options'],
    previewResult: 'A story format ready to embed, measure and update from the newsroom',
  },
  outcomesTitle: 'A newsroom-speed system for editorial and commercial products.',
  outcomes: [
    {
      title: 'Make stories participatory',
      text: 'Add questions, choices, visual explanations and useful paths that invite readers to do more than scroll.',
    },
    {
      title: 'Create premium commercial inventory',
      text: 'Build sponsor-ready products and branded experiences without separating the audience experience from the editorial system.',
    },
    {
      title: 'Own the audience signal',
      text: 'Capture choices, interests and completion events, then send consented data to approved analytics or audience systems.',
    },
  ],
  productsTitle: 'From the article body to the homepage.',
  productsIntro:
    'Build complete products for planned packages, fast-moving news and commercial partnerships, then place them wherever the audience already reads and watches.',
  products: [
    {
      title: 'Interactive article',
      text: 'Combine editorial text, video, data and participation in a responsive story that fits the publication’s design system.',
      signal: 'Reading behavior',
    },
    {
      title: 'In-article experience',
      text: 'Embed a poll, quiz, game or explainer directly inside an existing article or live blog.',
      signal: 'Audience response',
    },
    {
      title: 'Live news tracker',
      text: 'Bring status, maps, results and an evolving editorial narrative together in one updateable product.',
      signal: 'Live engagement',
    },
    {
      title: 'Digital magazine',
      text: 'Package stories, profiles, video and sponsor integrations into a premium standalone edition.',
      signal: 'Content interests',
    },
    {
      title: 'Homepage experience',
      text: 'Create a prominent interactive takeover or live component for a major editorial moment.',
      signal: 'Homepage actions',
    },
    {
      title: 'Sponsor-ready product',
      text: 'Give commercial partners a useful branded experience with defined placements, interactions and measurement.',
      signal: 'Partner reporting',
    },
  ],
  journey: {
    eyebrow: 'From source to published experience',
    title: 'Move at newsroom speed without giving up editorial control.',
    text:
      'Turn an approved article, URL or idea into a structured starting point, then let editors and designers refine it before it reaches the audience.',
    steps: [
      {
        label: '01',
        title: 'Start with the story',
        text: 'Use an article, live topic, commercial brief or editorial idea as the source for the experience.',
      },
      {
        label: '02',
        title: 'Choose the participation model',
        text: 'Structure the story as a poll, quiz, timeline, comparison, tracker or complete visual journey.',
      },
      {
        label: '03',
        title: 'Apply editorial and brand rules',
        text: 'Refine every detail in the Studio while approved components and publishing permissions stay in place.',
      },
      {
        label: '04',
        title: 'Publish where readers are',
        text: 'Launch as a destination or embed inside the publication’s sites and apps, then measure audience behavior.',
      },
    ],
  },
  operatingTitle: 'One platform across editorial, design and revenue teams.',
  operatingText:
    'Shared formats and protected systems help teams move quickly while the publication keeps ownership of its voice, presentation and audience data.',
  roles: [
    { team: 'Editorial', responsibility: 'Own the story, questions, updates and audience value.' },
    { team: 'Design', responsibility: 'Define responsive formats, components and the publication’s visual system.' },
    { team: 'Audience', responsibility: 'Plan distribution, participation and reader journeys across surfaces.' },
    { team: 'Commercial', responsibility: 'Build sponsor-ready products and define approved partner placements.' },
    { team: 'Data and product', responsibility: 'Define events, destinations, embed behavior and publishing access.' },
  ],
  related: ['retail', 'payments'],
  cta: {
    title: 'Turn the next editorial moment into an audience experience.',
    text: 'Bring one story, live format or commercial brief. We will help you map the product and how it fits your publishing workflow.',
  },
};

export const audienceSolutions = [agencySolution, publisherSolution];

export const homepageSolutions: SolutionCard[] = [
  industrySolutionBySlug.banking,
  industrySolutionBySlug.insurance,
  industrySolutionBySlug.payments,
  industrySolutionBySlug.healthcare,
  industrySolutionBySlug['real-estate'],
  industrySolutionBySlug.retail,
  industrySolutionBySlug.telecommunications,
  publisherSolution,
  agencySolution,
];
